import { css, SelectInput, styled } from 'canoe';
import { Field, FieldAttributes, useFormikContext } from 'formik';
import get from 'lodash/get';
import has from 'lodash/has';
import React, { useMemo } from 'react';

import { RuleGroupNames } from '@candu-ai/segments/types';
import { DefinitionFormikValues } from 'dashboard/pages/Segment/components/types';

const DefinitionObjectWrapper = styled.div<{ nested?: boolean }>`
  ${({ theme, nested }) => css`
    ${!nested &&
    css`
      margin-left: ${theme.spaces.s};
    `}
    .segment-definition-object-selector {
      [data-cy='list-item-label'] {
        color: ${theme.colors.OnyxGrayL30};
      }
    }
  `}
`;

type Item = {
  label: string;
  value: string;
};

interface DefinitionObjectSelectorProps {
  name: string;
  readOnly?: boolean;
  isSingleRule?: boolean;
  nested?: boolean;
}

export const DefinitionObjectSelector = ({
  name,
  readOnly,
  isSingleRule,
  nested,
}: DefinitionObjectSelectorProps) => {
  const { values, setFieldValue } = useFormikContext<DefinitionFormikValues>();

  const definitionObjectOptions = useMemo(
    () => [
      { label: 'AND', value: isSingleRule ? RuleGroupNames.Single : RuleGroupNames.All },
      { label: 'OR', value: RuleGroupNames.Any },
    ],
    [isSingleRule, values],
  );

  if (!has(values, name)) {
    setFieldValue(name, definitionObjectOptions[0].value, false);
  }

  const selected = useMemo(
    () => definitionObjectOptions?.find((option) => option.value === get(values, name)),
    [definitionObjectOptions],
  );

  return (
    <Field name={name}>
      {({ field }: FieldAttributes<any>) => (
        <DefinitionObjectWrapper nested={nested}>
          <SelectInput<Item>
            {...field}
            data-cy="segment-definition-object-selector"
            className="segment-definition-object-selector"
            id={`definition-object-${name}`}
            disabled={readOnly}
            selectedItem={selected}
            onSelect={(item: Item) => setFieldValue(name, item.value, false)}
            items={definitionObjectOptions}
            clearable={false}
          />
        </DefinitionObjectWrapper>
      )}
    </Field>
  );
};
